import React, { useState } from 'react';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import authService from '../services/authService';

interface RecoveryFormData {
  email: string;
}

const AccountRecovery: React.FC = () => {
  const [formData, setFormData] = useState<RecoveryFormData>({
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      if (!formData.email) {
        throw new Error('Por favor, informe seu e-mail');
      }

      const response = await authService.recoverPassword({
        email: formData.email
      });

      if (response.success) {
        console.log('Recovery email sent:', response.data);
        setIsSubmitted(true);
      } else {
        throw new Error(response.message || 'Erro ao enviar email de recuperação');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar solicitação. Tente novamente.');
      console.error('Account recovery error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-3xl font-display font-bold text-brown-800 mb-4">
              Sucesso!
            </h3>
            <p className="text-brown-600 mb-6">
              Recebemos sua solicitação de recuperação de conta para <strong>{formData.email}</strong>.
              Caso este email esteja cadastrado no sistema, vamos enviar um link para você redefinir sua senha.
            </p>
            <div className="grid grid-cols-1 gap-4">
              <Button
                onClick={() => setIsSubmitted(false)}
                variant="secondary"
                className="w-full"
              >
                Fazer Nova Solicitação
              </Button>
              <a href="/signin">
                <Button variant="primary" className="w-full">
                  Voltar ao Login
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-brown-800 mb-2">
            SantoGram
          </h1>
          <p className="text-brown-600">
            Recupere o acesso à sua conta
          </p>
        </div>

        {/* Recovery Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200">
          {/* Back link */}
          <div className="mb-6">
            <a
              href="/signin"
              className="flex items-center gap-2 text-brown-600 hover:text-brown-800 text-sm font-medium"
            >
              <ArrowLeft size={16} />
              Voltar ao Login
            </a>
          </div>

          <div className="mb-6 text-center">
            <h2 className="text-2xl font-display font-bold text-brown-800 mb-2">
              Recuperar Conta
            </h2>
            <p className="text-brown-600 text-sm">
              Informe seu e-mail para solicitar a recuperação da sua conta.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="relative">
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                label="E-mail da Conta"
                required
              />
              <Mail className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                'Solicitar Recuperação'
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-brown-500">
            Nossa equipe verificará sua solicitação e entrará em contato por e-mail.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountRecovery;